import './styles/CategoriesList.scss';

import { Add, Delete, Edit } from '@mui/icons-material';
import { Button, Grid, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Category } from '@thro-types/leihbar';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLeihbarCategoriesQuery, useLeihbarCategoriesMutation } from 'api/hooks/leihbar';
import { TilesOrListView, TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { usePermissionToCreate } from '../../helper';
import CategoryCard from '../CategoryCard';
import { CategoryModal } from '../CategoryModal';

export function CategoriesList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ableToCreate = usePermissionToCreate();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [categoryRef, setCategoryRef] = useState<Category | undefined>(undefined);

  const { data: categories, isLoading } = useLeihbarCategoriesQuery();
  const { deletion } = useLeihbarCategoriesMutation();

  const handleDelete = async (id?: number) => {
    try {
      if (!id) {
        return;
      }
      await deletion.mutateAsync(id);
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.category.alerts.successfulDelete()))
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.category.alerts.failedDelete())));
    }
  };

  const handleEditClick = category => {
    setCategoryRef(category);
    setFormModalOpen(true);
  };

  const handleDeleteClick = category => {
    setCategoryRef(category);
    setDeleteDialogOpen(true);
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={<Edit sx={{ color: 'lightblue' }} />}
          label={t(translations.common.edit())}
          onClick={() => handleEditClick(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<Delete sx={{ color: 'red' }} />}
          label={t(translations.common.delete())}
          onClick={() => handleDeleteClick(params.row)}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<Category> => {
    return [
      {
        flex: 4,
        headerName: t(translations.pages.leihbar.category.columns.name()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  const ControlButtons = () => {
    return (
      <Button
        startIcon={<Add />}
        variant="contained"
        className="categoriesList__actions--primary"
        onClick={() => {
          setCategoryRef(undefined);
          setFormModalOpen(true);
        }}
      >
        {t(translations.common.create())}
      </Button>
    );
  };

  return (
    <>
      <ErrorBoundary boundaryTag="categoriesModal">
        <Modal
          open={formModalOpen}
          onClose={() => setFormModalOpen(false)}
          headline={
            categoryRef?.id
              ? t(translations.pages.leihbar.category.create.editModalTitle())
              : t(translations.pages.leihbar.category.create.createModalTitle())
          }
          disabledCancel={disabledCancel}
          width="half"
        >
          <CategoryModal
            closeHandler={() => setFormModalOpen(false)}
            setDisabledCancel={setDisabledCancel}
            category={categoryRef}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t(translations.pages.leihbar.category.dialogs.deleteConfirm.title())}
        onClose={() => {
          setCategoryRef(undefined);
          setDeleteDialogOpen(false);
        }}
        onConfirm={() => {
          handleDelete(categoryRef?.id);
          setCategoryRef(undefined);
          setDeleteDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.leihbar.category.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ErrorBoundary boundaryTag="categoriesList">
        <Grid container>
          <TilesOrListView
            data={categories}
            renderTile={category => (
              <CategoryCard
                category={category}
                handleEditClick={handleEditClick}
                handleDeleteClick={handleDeleteClick}
              />
            )}
            columns={columns.map(column => ({
              ...column,
              sortable: false
            }))}
            tilesPerRow={4}
            height={'70vh'}
            controlElements={
              ableToCreate && (
                <Grid item>
                  <ControlButtons />
                </Grid>
              )
            }
            isLoading={isLoading}
          />
        </Grid>
      </ErrorBoundary>
    </>
  );
}
